import { ExportReq, HttpService } from "@/core";
import { ExportResp, Records } from "@/core/interface";
import { generateUuid } from "@/utils";
import {
  BudgetPageReq,
  BudgetPageResp,
  BudgetDetailResp,
  BudgetReq,
  CategoryRecord,
  QuantityRecord,
  MaterialRecord,
  QuantityCheckResp,
  SubcontractRecord,
  LeaseRecord,
} from "./budget.interface";

export class BudgetService {
  static usePageApi() {
    const api = (params: BudgetPageReq) =>
      HttpService.axios({
        method: "GET",
        url: "/project_work_budget/page",
        params,
      });
    return HttpService.usePagination<BudgetPageResp>(api);
  }

  static useDetailApi() {
    const api = (params) =>
      HttpService.axios({
        method: "GET",
        url: `/project_work_budget/${params.projectWorkBudgetId}`,
        params
      });
    return HttpService.useRequest<BudgetDetailResp>(api);
  }

  static useCategoryDetailApi() {
    const api = (params) =>
      HttpService.axios({
        method: "GET",
        url: "/project_work_budget_category/list",
        params: params,
      });
    return HttpService.useRequest<Records<CategoryRecord>>(api);
  }

  static useCategoryImportApi() {
    const api = (url: string) =>
      HttpService.axios({
        method: "POST",
        url: "/project_work_budget_category/import/category",
        data: { url },
      });
    return HttpService.useRequest<Records<CategoryRecord>>(api);
  }

  static useCreateApi() {
    const api = (data: BudgetReq) =>
      HttpService.axios({
        method: "POST",
        url: "/project_work_budget",
        data,
      });
    return HttpService.useRequest<{ id: string }>(api);
  }

  static useEditApi() {
    const api = (id: string, data: BudgetReq) =>
      HttpService.axios({
        method: "PUT",
        url: `/project_work_budget/${id}`,
        data,
      });
    return HttpService.useRequest<{ id: string }>(api);
  }

  static useDeleteApi() {
    const api = (id: string) =>
      HttpService.axios({
        method: "DELETE",
        url: `/project_work_budget/${id}`,
      });
    return HttpService.useRequest<void>(api);
  }

  static useExportApi() {
    const api = (ids: ExportReq) =>
      HttpService.axios({
        method: "GET",
        url: "/project_work_budget/export",
        params: { ids },
      });
    return HttpService.useRequest<ExportResp>(api);
  }

  static useQuantityImportApi() {
    const api = (url: string) =>
      HttpService.axios({
        method: "POST",
        url: "/project_work_budget_quantities/import/quantities",
        data: { url },
      });
    return HttpService.useRequest<Records<QuantityRecord>>(api);
  }

  static useQuantityAddApi() {
    const api = (data) =>
      HttpService.axios({
        method: "POST",
        url: "/project_work_budget_quantities/addByProjectId",
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify(data),
      });
    return HttpService.useRequest<Records<QuantityRecord>>(api);
  }

  static useQuantityDetailApi() {
    const api = (params) =>
      HttpService.axios({
        method: "GET",
        url: "/project_work_budget_quantities/list",
        params,
      });
    return HttpService.useRequest<Records<QuantityRecord>>(api);
  }

  static useQuantityExportApi() {
    const api = (budgetId: string) =>
      HttpService.axios({
        method: "GET",
        url: `/project_work_budget_quantities/${budgetId}/export/quantities`,
      });
    return HttpService.useRequest<ExportResp>(api);
  }

  static useQuantityListExportApi() {
    const api = (budgetId: string) =>
      HttpService.axios({
        method: "GET",
        url: `/project_work_budget_quantities/${budgetId}/export`,
      });
    return HttpService.useRequest<ExportResp>(api, { isOutError: true });
  }

  static useQuantityCheckApi() {
    const api = (projectId: string) =>
      HttpService.axios({
        method: "GET",
        url: `/project_work_budget_quantities/${projectId}/checkByProjectId`,
      });
    return HttpService.useRequest<QuantityCheckResp>(api);
  }

  static useMaterialImportApi() {
    const api = (url: string) =>
      HttpService.axios({
        method: "POST",
        url: "/project_work_budget_material/import/material",
        data: { url },
      });
    return HttpService.useRequest<Records<MaterialRecord>>(api);
  }

  static useMaterialDetailApi() {
    const api = (params) =>
      HttpService.axios({
        method: "GET",
        url: "/project_work_budget_material/list",
        params,
      });
    return HttpService.useRequest<Records<MaterialRecord>>(api);
  }

  static useMaterialExportApi() {
    const api = (budgetId: string) =>
      HttpService.axios({
        method: "GET",
        url: `/project_work_budget_material/${budgetId}/export/quantities`,
      });
    return HttpService.useRequest<ExportResp>(api);
  }

  static useMaterialListExportApi() {
    const api = (budgetId: string) =>
      HttpService.axios({
        method: "GET",
        url: `/project_work_budget_material/${budgetId}/export`,
      });
    return HttpService.useRequest<ExportResp>(api, { isOutError: true });
  }

  static useLeaseDetailApi() {
    const api = (params) =>
      HttpService.axios({
        method: "GET",
        url: "/project_work_budget/budgetLeaseList",
        params,
      });
    return HttpService.useRequest<Records<LeaseRecord>>(api);
  }

  static useSubDetailApi() {
    const api = (params) =>
      HttpService.axios({
        method: "GET",
        url: "/project_work_budget/budgetSubList",
        params,
      });
    return HttpService.useRequest<Records<SubcontractRecord>>(api);
  }

  static async fetchBudgetDetail(budgetId: string) {
    const req = new BudgetReq();
    let changeCount=0;
    try {
      // 获取主体信息
      const { run: runBase } = BudgetService.useDetailApi();
      const baseRes = await runBase({ projectWorkBudgetId: budgetId });
      if (baseRes.respCode === "0000") {
        changeCount=baseRes.data?.changeCount||0;
        req.merge(baseRes.data);
      }

      // 获取类目明细
      const { run: runCategory } = BudgetService.useCategoryDetailApi();
      const categoryRes = await runCategory({ projectWorkBudgetId: budgetId,changeIndex:changeCount });
      if (categoryRes.respCode === "0000") {
        const categoryAReqs = (categoryRes.data?.records || []).map((item) => {
          item.uuid = generateUuid();
          const record = new CategoryRecord();
          record.merge(item);
          return record;
        });
        req.merge({ categoryAReqs });
      }

      // 获取工程量清单
      const { run: runQuantity } = BudgetService.useQuantityDetailApi();
      const quantityRes = await runQuantity({ projectWorkBudgetId: budgetId,changeIndex:changeCount });
      if (quantityRes.respCode === "0000") {
        const quantitiesAReqs = (quantityRes.data?.records || []).map((item) => {
          item.uuid = generateUuid();
          const record = new QuantityRecord();
          record.merge(item);
          return record;
        });
        req.merge({ quantitiesAReqs });
      }

      // 获取材料清单
      const { run: runMaterial } = BudgetService.useMaterialDetailApi();
      const materialRes = await runMaterial({ projectWorkBudgetId: budgetId,changeIndex:changeCount });
      if (materialRes.respCode === "0000") {
        const materialAReqs = (materialRes.data?.records || []).map((item) => {
          item.uuid = generateUuid();
          const record = new MaterialRecord();
          record.merge(item);
          return record;
        });
        req.merge({ materialAReqs });
      }

      // 获取租赁明细
      const { run: runLease } = BudgetService.useLeaseDetailApi();
      const leaseRes = await runLease({ projectWorkBudgetId: budgetId,changeIndex:changeCount });
      if (leaseRes.respCode === "0000") {
        const budgetLeaseReq = (leaseRes.data?.records || []).map((item) => {
          item.uuid = generateUuid();
          const record = new LeaseRecord();
          record.merge(item);
          return record;
        });
        req.merge({ budgetLeaseReq });
      }

      // 获取分包明细
      const { run: runSub } = BudgetService.useSubDetailApi();
      const subRes = await runSub({ projectWorkBudgetId: budgetId,changeIndex:changeCount });
      if (subRes.respCode === "0000") {
        const budgetSubReq = (subRes.data?.records || []).map((item) => {
          item.uuid = generateUuid();
          const record = new SubcontractRecord();
          record.merge(item);
          return record;
        });
        req.merge({ budgetSubReq });
      }
      console.log(req);
    } catch (e: any) {
      //
    }
    return req;
  }

  static validCategory(list: CategoryRecord[] = []) {
   
    let valid = true;
    if (list.length === 0) {
      return false;
    }
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      delete item['_X_ROW_KEY']

      if (!item.projectFundId || !item.budget) {
        valid = false;
        break;
      }
    }
    return valid;
  }

  static validQuantity(list: QuantityRecord[] = []) {
    if (list.length === 0) {
      return true;
    }
    let valid = true;
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      delete item['_X_ROW_KEY']

      if (!item.name) {
        valid = false;
        break;
      }
      if (!item.price&&item.price!==0) {
        valid = false;
        break;
      }
      if (!item.count&&item.count!==0) {
        valid = false;
        break;
      }
      if (!item.unit) {
        valid = false;
        break;
      }
    }
    return valid;
  }

  static validMaterial(list: MaterialRecord[] = []) {
   
    let valid = true;
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      delete item['_X_ROW_KEY']
      if (
        !(
          item.materialName &&
          item.materialBrand &&
          item.materialModel &&
          item.materialUnit &&
          item.count &&          
          item.demandTime
        )
      ) {
        valid = false;
        break;
      }
    }
    return valid;
  }
  static validSubcontract(list: SubcontractRecord[] = []) {
   
    let valid = true;
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      delete item['_X_ROW_KEY']

      if (
        !(
          item.subtask &&
          item.subBudget
        )
      ) {
        valid = false;
        break;
      }
    }
    return valid;
  }
  static validLease(list: LeaseRecord[] = []) {
   
    let valid = true;
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      delete item['_X_ROW_KEY']

      if (
        !(
          item.leaseTask &&
          item.leaseBudget
        )
      ) {
        valid = false;
        break;
      }
    }
    return valid;
  }
}

